/**
 * Eric (2026-06-15 — iter102) — Helpers d'affichage extraits de VideoDetailPage.js
 * (qui faisait 5723 lignes). Premier lot de la refacto progressive : helpers
 * purs et constantes statiques (zéro state React). Les composants/onglets
 * suivront en LOTs ultérieurs pour minimiser les risques.
 *
 * - TYPE_ICONS / TYPE_COLORS : style des badges par type d'unité (qa, procedure…)
 * - SPEAKER_COLORS           : couleurs cycliques pour la diarisation
 * - PIPELINE_STAGES          : étapes + pourcentages de la pipeline d'ingestion
 * - SPEED_OPTIONS            : vitesses de lecture du player vidéo
 * - formatTime               : "1:23" ou "1:23:45" depuis secondes
 * - formatTimeBracket        : "[01:23]" ou "[01:23:45]" pour transcript 3 colonnes
 * - highlightText            : surligne (mark) les occurrences d'une query dans un texte
 */
import {
  Question,
  ListChecks,
  Microphone,
  ShieldWarning,
  Lightbulb,
} from "@phosphor-icons/react";

export const TYPE_ICONS = {
  qa: Question,
  procedure: ListChecks,
  script: Microphone,
  objection: ShieldWarning,
  rebuttal: ShieldWarning,
  concept: Lightbulb,
};

export const TYPE_COLORS = {
  // Vincent B876 (Eric 2026-02-14 SPEC STRICK PROD) — Palette texte
  // remontée aux variants -700 pour passer WCAG AA (≥ 4.5:1) sur les
  // fonds bg-secondary/-zinc-100 des chips d'onglets. Le fond garde
  // les rgba 0.15 (ambiance colorée pastel) mais le texte devient
  // franchement lisible. Border également -700 pour cohérence.
  qa: { bg: "rgba(59,130,246,0.15)", color: "#1d4ed8", border: "rgba(29,78,216,0.4)" },
  procedure: { bg: "rgba(34,197,94,0.15)", color: "#15803d", border: "rgba(21,128,61,0.4)" },
  // Vincent B875-final (Eric 2026-02-14) — Scripts : #a16207 (yellow-700)
  // affichait 4.48:1 sur bg-zinc-100 (juste sous le seuil AA de 4.5). On
  // descend à yellow-800 (#854d0e) : contraste ≈ 6.3:1, largement AA.
  script: { bg: "rgba(234,179,8,0.15)", color: "#854d0e", border: "rgba(133,77,14,0.4)" },
  objection: { bg: "rgba(239,68,68,0.15)", color: "#b91c1c", border: "rgba(185,28,28,0.4)" },
  rebuttal: { bg: "rgba(168,85,247,0.15)", color: "#7e22ce", border: "rgba(126,34,206,0.4)" },
  concept: { bg: "rgba(99,102,241,0.15)", color: "#4338ca", border: "rgba(67,56,202,0.4)" },
  // Vincent B6 XLS 2026-08-10 (Eric 2026-02-10) — Synthèse : couleur teal
  // (#14b8a6, présente dans SPEAKER_COLORS mais non utilisée par d'autres
  // KU types). Cohérente avec la palette de la timeline vidéo et
  // distincte des 6 types existants. B876 → teal-700 pour AA.
  synthesis: { bg: "rgba(20,184,166,0.15)", color: "#0f766e", border: "rgba(15,118,110,0.4)" },
};

export const SPEAKER_COLORS = [
  "#3b82f6", "#22c55e", "#eab308", "#ef4444", "#a855f7",
  "#ec4899", "#14b8a6", "#f97316",
];

export const PIPELINE_STAGES = {
  // Tâche 1358 (Eric 2026-06-30) — Les libellés d'étapes étaient en français
  // en dur : la barre du pipeline restait donc française en version anglaise.
  // Les deux langues vivent sur la MÊME ligne (motif retenu en 1324b) : deux
  // tables jumelles divergent au premier ajout d'étape.
  uploaded: { label: "En attente", label_en: "Waiting", pct: 0 },
  // Vincent (2026-02) ticket #520 — Renommage EXACT selon spec :
  //   "Extraction de l'audio", "Transcription (Whisper)",
  //   "Diarisation (AssemblyAI)", "Fusion des transcriptions",
  //   "Extraction des connaissances (GPT)", "Indexation DB".
  //   Ces labels sont utilisés par VideoPipelineStatus (dots + tooltip)
  //   et propagés côté backend (content_access.py STAGE_LABEL_FR).
  audio_extraction: { label: "Extraction de l'audio", label_en: "Audio extraction", pct: 15 },
  transcription: { label: "Transcription (Whisper)", label_en: "Transcript (Whisper)", pct: 35 },
  diarization: { label: "Diarisation (AssemblyAI)", label_en: "Diarization (AssemblyAI)", pct: 55 },
  // Bug 536 (Eric 2026-02-14) — Étape UI-only « Identification faciale »
  // insérée entre Diarisation et Fusion. Techniquement réalisée par le
  // backend DANS l'étape diarization (via _run_speakers_reprocess_task +
  // reconnaissance visuelle), mais l'utilisateur veut la voir séparée
  // dans la barre pour clarté (et pouvoir la voir en rouge quand
  // `skip_visual_speaker_id=true`). Non-marquée `pseudo` pour rester dans
  // les dots ; le pct 60 la place visuellement entre diarization (55)
  // et fusion (65).
  facial_id: { label: "Identification faciale", label_en: "Facial identification", pct: 60 },
  // Pseudo-stages de pause (invisibles dans les dots — cf. flag `pseudo`)
  transcription_complete: { label: "Transcription terminée", label_en: "Transcript complete", pct: 55, pseudo: true },
  awaiting_fusion: { label: "En pause — révision fusion", label_en: "Paused — fusion review", pct: 60, pseudo: true },
  fusion_running: { label: "Fusion des transcriptions", label_en: "Transcript fusion", pct: 65, pseudo: true },
  // Étape Fusion visible dans les dots (à sa position 65%, entre diarisation et extraction)
  fusion: { label: "Fusion des transcriptions", label_en: "Transcript fusion", pct: 65 },
  awaiting_extraction: { label: "En pause — lancer extraction", label_en: "Paused — start extraction", pct: 70, pseudo: true },
  extraction: { label: "Extraction des connaissances (GPT)", label_en: "Knowledge extraction (GPT)", pct: 80 },
  embedding: { label: "Indexation DB", label_en: "DB indexing", pct: 92 },
  complete: { label: "Terminé", label_en: "Complete", pct: 100 },
};

/** Tâche 1358 — Libellé d'une étape dans la langue en cours. */
export function pipelineStageLabel(stage, lang) {
  const val = PIPELINE_STAGES[stage] || PIPELINE_STAGES.uploaded;
  return (lang === "en" ? val.label_en : val.label) || val.label;
}

export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Vincent #100 (2026-05-12) — Format horodatage entre crochets, padding 2 chiffres
// partout. [MM:SS] si < 1h, sinon [HH:MM:SS]. Utilisé pour les transcriptions
// en 3 colonnes (timecode | locuteur | citation).
export function formatTimeBracket(seconds) {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return "[00:00]";
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  if (h > 0) return `[${pad(h)}:${pad(m)}:${pad(s)}]`;
  return `[${pad(m)}:${pad(s)}]`;
}

// Surligne les occurrences de `query` dans `text` (case-insensitive).
// Retourne un tableau de chaînes et de <mark> JSX prêts à rendre.
export function highlightText(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-200/80 text-yellow-900 rounded px-0.5">{part}</mark>
    ) : (
      part
    )
  );
}
